import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, AlertTriangle, Copy, Save, Wand2, Info } from "lucide-react";

const NONE = "__none__";

function norm(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export default function ImportViaggiDialog({ open, onOpenChange, config, onImported }) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validated, setValidated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState("");
  const [autoCreate, setAutoCreate] = useState(true);
  const fileRef = useRef();

  const targetFields = config?.import_fields || [];

  useEffect(() => {
    if (open) {
      setStep("upload");
      setFile(null);
      setColumns([]);
      setMapping({});
      setValidated(null);
      setResult(null);
      setProfileName("");
      api.get("/viaggi/import-profiles").then((r) => setProfiles(r.data)).catch(() => {});
    }
  }, [open]);

  const autoMap = (cols) => {
    const m = {};
    const used = new Set();
    // pass 1: exact alias / name match; pass 2: fuzzy contains
    targetFields.forEach((tf) => {
      const keys = [tf.name, ...(tf.aliases || [])].map(norm);
      const found = cols.find((c) => !used.has(c) && keys.includes(norm(c)));
      if (found) { m[tf.name] = found; used.add(found); }
    });
    targetFields.forEach((tf) => {
      if (m[tf.name]) return;
      const keys = [tf.name, ...(tf.aliases || [])].map(norm).filter((k) => k.length >= 4);
      const found = cols.find((c) => !used.has(c) && keys.some((k) => norm(c).includes(k) || k.includes(norm(c))));
      if (found) { m[tf.name] = found; used.add(found); }
    });
    return m;
  };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/viaggi/import/preview", fd);
      setColumns(data.columns);
      setMapping(autoMap(data.columns));
      setStep("map");
      toast.success(`File letto (${data.total_rows} righe, codifica ${data.encoding})`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/viaggi/import/preview", fd);
      setValidated(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("auto_create", String(autoCreate));
      const { data } = await api.post("/viaggi/import/commit", fd);
      setResult(data);
      setStep("result");
      onImported?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!profileName) {
      toast.error("Inserisci un nome per il profilo");
      return;
    }
    try {
      await api.post("/viaggi/import-profiles", { name: profileName, mapping });
      toast.success("Profilo salvato");
      setProfileName("");
      api.get("/viaggi/import-profiles").then((r) => setProfiles(r.data));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyProfile = (pid) => {
    const p = profiles.find((x) => x.id === pid);
    if (p) {
      setMapping(p.mapping);
      toast.success(`Profilo "${p.name}" applicato`);
    }
  };

  const counts = validated?.counts;
  const rows = validated?.validated || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Importa viaggi da CSV / Excel</DialogTitle>
          <DialogDescription>
            Mappa le colonne del file ai campi del viaggio, valida e importa solo le righe corrette.
            Il codice viaggio è opzionale (generato automaticamente); i duplicati vengono esclusi in base a data, targa e DDT/tratta.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="py-8">
            <div
              className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center cursor-pointer hover:border-sky-400 transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid="import-dropzone"
            >
              {busy ? (
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-500" />
              ) : (
                <Upload className="h-8 w-8 mx-auto text-slate-400" />
              )}
              <div className="mt-3 text-sm text-slate-600">
                Clicca per selezionare un file <strong>.csv</strong> o <strong>.xlsx</strong>
              </div>
              <div className="text-xs text-slate-400 mt-1">Codifica UTF-8 o Windows-1252 (rilevata automaticamente)</div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,.txt"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </div>
        )}

        {step === "map" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {profiles.length > 0 && (
                <>
                  <Label className="text-xs whitespace-nowrap">Profilo salvato</Label>
                  <Select onValueChange={applyProfile}>
                    <SelectTrigger className="h-9 w-64" data-testid="import-profile-select"><SelectValue placeholder="Applica un profilo..." /></SelectTrigger>
                    <SelectContent>
                      {profiles.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </>
              )}
              <Button variant="outline" size="sm" className="h-9" onClick={() => setMapping(autoMap(columns))} data-testid="import-automap">
                <Wand2 className="h-4 w-4 mr-1.5" />Auto-mappa
              </Button>
              <span className="text-xs text-slate-500 ml-auto">{columns.length} colonne nel file</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {targetFields.map((tf) => (
                <div key={tf.name} className="flex items-center gap-2">
                  <Label className="text-xs w-44 shrink-0">
                    {tf.label}{tf.required && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  <Select
                    value={mapping[tf.name] || NONE}
                    onValueChange={(v) => setMapping((p) => ({ ...p, [tf.name]: v === NONE ? undefined : v }))}
                  >
                    <SelectTrigger className="h-9" data-testid={`import-map-${tf.name}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Non mappare —</SelectItem>
                      {columns.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} data-testid="import-auto-create" className="accent-sky-600" />
              Crea automaticamente in anagrafica clienti, località e prodotti non trovati (autisti e targhe sconosciute vengono salvati come testo)
            </label>

            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <Input className="h-9 w-56" placeholder="Nome profilo di importazione" value={profileName} onChange={(e) => setProfileName(e.target.value)} data-testid="import-profile-name" />
              <Button variant="outline" size="sm" className="h-9" onClick={saveProfile} data-testid="import-save-profile">
                <Save className="h-4 w-4 mr-1.5" />Salva profilo
              </Button>
              <Button size="sm" className="h-9 ml-auto" onClick={validate} disabled={busy} data-testid="import-validate">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Valida
              </Button>
            </div>

            {counts && (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <div className="badge-verde rounded-md p-3 border">
                    <div className="text-xs font-semibold uppercase flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />Importabili</div>
                    <div className="text-2xl font-head font-bold" data-testid="import-count-ok">{counts.ok}</div>
                  </div>
                  <div className="rounded-md p-3 border bg-sky-50 border-sky-200 text-sky-800">
                    <div className="text-xs font-semibold uppercase flex items-center gap-1"><Info className="h-4 w-4" />Con avvisi</div>
                    <div className="text-2xl font-head font-bold" data-testid="import-count-warning">{counts.warning}</div>
                  </div>
                  <div className="badge-giallo rounded-md p-3 border">
                    <div className="text-xs font-semibold uppercase flex items-center gap-1"><Copy className="h-4 w-4" />Duplicati</div>
                    <div className="text-2xl font-head font-bold" data-testid="import-count-duplicate">{counts.duplicate}</div>
                  </div>
                  <div className="badge-rosso rounded-md p-3 border">
                    <div className="text-xs font-semibold uppercase flex items-center gap-1"><AlertTriangle className="h-4 w-4" />Errori</div>
                    <div className="text-2xl font-head font-bold" data-testid="import-count-error">{counts.error}</div>
                  </div>
                </div>
                {(counts.duplicate > 0 || counts.error > 0 || counts.warning > 0) && (
                  <div className="border border-slate-200 rounded-md max-h-48 overflow-auto text-xs" data-testid="import-issues-table">
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-2 py-1">Riga</th><th className="text-left px-2 py-1">Stato</th><th className="text-left px-2 py-1">Dettaglio</th></tr></thead>
                      <tbody>
                        {rows.filter((r) => r.status !== "ok").map((r) => (
                          <tr key={r.row} className="border-t border-slate-100">
                            <td className="px-2 py-1">{r.row}</td>
                            <td className="px-2 py-1">{r.status === "duplicate" ? "Duplicato" : r.status === "warning" ? "Avviso" : "Errore"}</td>
                            <td className="px-2 py-1 text-slate-600">{r.status === "duplicate" ? r.duplicate_reason : r.status === "warning" ? r.warnings.join("; ") : r.errors.join("; ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("upload")}>Indietro</Button>
              <Button onClick={commit} disabled={busy || !counts || counts.ok === 0} data-testid="import-commit">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Importa {counts ? `${counts.ok} viaggi validi` : ""}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-4 py-2" data-testid="import-result">
            <div className="grid grid-cols-4 gap-3">
              <div className="badge-verde rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Importati</div><div className="text-2xl font-head font-bold" data-testid="import-result-inserted">{result.inserted}</div></div>
              <div className="rounded-md p-3 border bg-sky-50 border-sky-200 text-sky-800"><div className="text-xs font-semibold uppercase">Con avvisi</div><div className="text-2xl font-head font-bold">{result.warnings}</div></div>
              <div className="badge-giallo rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Duplicati saltati</div><div className="text-2xl font-head font-bold">{result.skipped_duplicates}</div></div>
              <div className="badge-rosso rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Errori</div><div className="text-2xl font-head font-bold">{result.errors}</div></div>
            </div>
            <p className="text-sm text-slate-600">{result.inserted} viaggi importati correttamente. I duplicati non sono stati reinseriti.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("map")}>Nuova validazione</Button>
              <Button onClick={() => onOpenChange(false)} data-testid="import-close">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
